import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Pill, Btn } from "@/components/kit";
import { usd } from "@/lib/format";
import { FileSignature, Lock, Sparkles, Loader2, RefreshCw, Ruler, FileUp, Map, Globe } from "lucide-react";
import { toast } from "sonner";
import MeasureMap from "@/components/MeasureMap";
import { ROOF_TYPES, tLabel } from "@/lib/roofTypes";

const TIER_TONE = { SHINGLE: "navy", METAL: "slate", TILE: "red", FLAT: "slate", GOOD: "slate", BETTER: "navy", BEST: "red" };

export default function Estimating() {
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState("");
  const [squares, setSquares] = useState("");
  const [footprint, setFootprint] = useState("");
  const [reasoning, setReasoning] = useState("");
  const [estimate, setEstimate] = useState(null);
  const [tier, setTier] = useState("SHINGLE");
  const [measuring, setMeasuring] = useState(false);
  const [blueprintBusy, setBlueprintBusy] = useState(false);
  const [measureOpen, setMeasureOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const blueprintRef = useRef(null);
  const nav = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => { api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {}); }, []);

  // Deep-link from a property's "Price this" — preselect the customer and price its latest measurement.
  useEffect(() => {
    const cid = params.get("contact");
    if (cid) { setContactId(cid); buildFrom(cid); }
  }, []); // eslint-disable-line

  const buildFrom = async (cid, sq) => {
    setBusy(true); setEstimate(null);
    try {
      const payload = { contact_id: cid };
      if (sq !== undefined && sq !== null && sq !== "") payload.squares = Number(sq);
      const { data } = await api.post("/estimates", payload);
      setEstimate(data); setSquares(String(data.squares)); setTier("SHINGLE");
      toast.success(`Priced by roof type from ${data.squares} squares`);
    } catch (e) { toast.error(e.response?.data?.detail || "Estimate failed"); }
    finally { setBusy(false); }
  };

  // AI Auto-Measure: estimate roof size from address (or exact footprint), then price.
  const autoMeasure = async (cid, footprintVal) => {
    if (!cid) return;
    setMeasuring(true); setEstimate(null); setReasoning("");
    try {
      const payload = { contact_id: cid };
      if (footprintVal) payload.building_sqft = Number(footprintVal);
      const { data } = await api.post("/takeoff/ai", payload);
      setSquares(String(data.squares));
      setReasoning(data.reasoning || "");
      await buildFrom(cid, data.squares);
    } catch (e) { toast.error(e.response?.data?.detail || "Auto-measure failed"); }
    finally { setMeasuring(false); }
  };

  // Blueprint Takeoff: a vision model reads dimensions/scale off an uploaded blueprint.
  const uploadBlueprint = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!contactId) { toast.error("Pick a customer first"); e.target.value = ""; return; }
    setBlueprintBusy(true); setEstimate(null); setReasoning("");
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("contact_id", contactId);
      const { data } = await api.post("/takeoff/blueprint", fd);
      setSquares(String(data.squares));
      setReasoning(`Blueprint takeoff${data.sheet_used ? ` (${data.sheet_used})` : ""} — ${data.reasoning}${data.dimensions_found ? ` · dimensions: ${data.dimensions_found}` : ""}${data.page_count > 1 ? ` · scanned ${data.pages_scanned}/${data.page_count} pages` : ""}`);
      await buildFrom(contactId, data.squares);
      toast.success(`Blueprint takeoff: ${data.squares} squares`);
    } catch (err) { toast.error(err.response?.data?.detail || "Blueprint read failed"); }
    finally { setBlueprintBusy(false); e.target.value = ""; }
  };

  const onSelectCustomer = (cid) => {
    setContactId(cid); setSquares(""); setFootprint(""); setReasoning(""); setEstimate(null);
    if (cid) buildFrom(cid); // free: uses any saved measurement, else a placeholder — pick a takeoff method for accuracy
  };

  const toProposal = async () => {
    await api.post("/proposals", { estimate_id: estimate.id, selected_tier: tier });
    toast.success("Proposal generated"); nav("/proposals");
  };

  const working = measuring || busy;
  const selContact = contacts.find((c) => c.id === contactId);

  return (
    <div>
      <PageHeader title="Takeoff & Estimating" subtitle="Measure by AI aerial (rough, address-based), Blueprint upload (exact), or known footprint — then price by roof type (Shingle / Metal / Tile / Flat). Formula: Qty × Material + Labor + Overhead + Margin = Selling Price" />

      <div className="mb-card p-5 mb-6">
        <div className="grid sm:grid-cols-4 gap-3 items-end">
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Customer</label>
            <select data-testid="est-contact" value={contactId} onChange={(e) => onSelectCustomer(e.target.value)}
              className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-navy">
              <option value="">Select a customer to auto-estimate…</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}{c.address ? ` — ${c.address}` : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Roof Size (squares)</label>
            <input data-testid="est-squares" type="number" value={squares} onChange={(e) => setSquares(e.target.value)}
              placeholder="Auto-measured"
              className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-navy font-num" />
          </div>
          <Btn onClick={() => buildFrom(contactId, squares)} disabled={working || !contactId || !squares} testid="build-estimate">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Re-price
          </Btn>
        </div>

        {/* AI takeoff row */}
        <div className="grid sm:grid-cols-4 gap-3 items-end mt-3">
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Known Building Footprint (sq ft, optional — for exact size)</label>
            <input data-testid="est-footprint" type="number" value={footprint} onChange={(e) => setFootprint(e.target.value)}
              placeholder="e.g. 8,500"
              className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-navy font-num" />
          </div>
          <Btn variant="red" onClick={() => autoMeasure(contactId, footprint)} disabled={measuring || !contactId} testid="ai-measure-btn">
            {measuring ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ruler className="w-4 h-4" />} AI Auto-Measure
          </Btn>
          <div className="flex items-center gap-2 text-xs font-semibold text-navy">
            <Lock className="w-3.5 h-3.5 text-successg" /> Margin lock ON — no line below 30% GP
          </div>
        </div>

        {/* Blueprint takeoff — vision model reads real dimensions/scale */}
        <div className="mt-3 border-2 border-dashed border-slate-300 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-semibold text-navy text-sm flex items-center gap-2"><FileUp className="w-4 h-4" /> Takeoff from Blueprint</div>
            <div className="text-xs text-slate-500">Upload a roof plan / blueprint (PDF or image). AI reads the dimensions & scale to compute exact squares.</div>
          </div>
          <input ref={blueprintRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={uploadBlueprint} data-testid="blueprint-input" />
          <Btn variant="ghost" onClick={() => blueprintRef.current?.click()} disabled={!contactId || blueprintBusy} testid="blueprint-btn">
            {blueprintBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />} Upload Blueprint & Measure
          </Btn>
        </div>

        {/* Free satellite measure + Google Earth double-check */}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Btn onClick={() => (contactId ? setMeasureOpen(true) : toast.error("Pick a customer first"))} testid="satellite-measure-btn">
            <Map className="w-4 h-4" /> Measure on Satellite (free)
          </Btn>
          {selContact?.address && (
            <a data-testid="google-earth-link" target="_blank" rel="noreferrer"
              href={`https://earth.google.com/web/search/${encodeURIComponent(selContact.address)}`}
              className="text-sm font-semibold text-navy underline flex items-center gap-1"><Globe className="w-4 h-4" /> Open in Google Earth</a>
          )}
          <span className="text-xs text-slate-400">Trace the roof on live satellite imagery — free & accurate. EagleView / Roofr / GAF QuickMeasure (paid) can be connected later.</span>
        </div>

        {blueprintBusy && (
          <div className="mt-3 flex items-center gap-2 text-sm text-slate-500"><FileUp className="w-4 h-4 text-brandred animate-pulse" /> Reading blueprint dimensions and computing the roof takeoff…</div>
        )}
        {measuring && (
          <div className="mt-4 flex items-center gap-2 text-sm text-slate-500"><Ruler className="w-4 h-4 text-brandred animate-pulse" /> AI aerial estimate — approximating roof size from the address…</div>
        )}
        {reasoning && !measuring && !blueprintBusy && (
          <div className="mt-3 rounded-xl bg-amberw/10 border-2 border-amberw/30 p-3 text-sm text-amberw flex items-start gap-2" data-testid="takeoff-note">
            <Sparkles className="w-4 h-4 mt-0.5 shrink-0" /> <span><b>Estimated {squares} squares.</b> {reasoning}. For an exact takeoff, upload a blueprint above or enter the known building footprint.</span>
          </div>
        )}
      </div>

      {measureOpen && (
        <MeasureMap address={selContact?.address || ""} onClose={() => setMeasureOpen(false)}
          onDone={async (sq, pitch) => {
            setMeasureOpen(false);
            try {
              await api.post("/takeoff/manual", { contact_id: contactId, squares: sq, pitch, source: "Satellite Measure" });
              setReasoning(`Satellite trace — you measured ${sq} squares at ${pitch} pitch`);
              await buildFrom(contactId, sq);
            } catch (err) { toast.error(err.response?.data?.detail || "Could not save measurement"); }
          }} />
      )}

      {estimate && (
        <>
          <div className="grid lg:grid-cols-3 gap-4 mb-6">
            {ROOF_TYPES.map((t) => {
              const v = estimate.variants[t];
              if (!v) return null;
              const low = v.gp_pct < 30;
              return (
                <button key={t} data-testid={`variant-${t}`} onClick={() => setTier(t)}
                  className={`text-left mb-card p-5 border-2 transition-all ${tier === t ? "border-navy shadow-lg -translate-y-1" : "border-transparent"}`}>
                  <div className="flex justify-between items-center mb-2"><Pill tone={TIER_TONE[t]}>{tLabel(t)}</Pill>
                    {low ? <Pill tone="danger">⚠ {v.gp_pct}% GP</Pill> : <span className="text-xs font-num text-successg font-bold">{v.gp_pct}% GP</span>}</div>
                  <div className="font-num text-3xl font-extrabold text-navy">{usd(v.total_cents)}</div>
                  <div className="text-xs text-slate-400 mt-1">Cost {usd(v.cost_cents)}</div>
                </button>
              );
            })}
          </div>

          <div className="mb-card overflow-x-auto mb-scroll mb-4">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr><th className="px-4 py-3">Line ({tLabel(tier)})</th><th className="px-4 py-3 text-right">Qty</th><th className="px-4 py-3 text-right">Unit Cost</th><th className="px-4 py-3 text-right">Cost</th><th className="px-4 py-3 text-right">Markup</th><th className="px-4 py-3 text-right">Margin</th><th className="px-4 py-3 text-right">Selling</th></tr>
              </thead>
              <tbody>
                {estimate.variants[tier].lines.map((l, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-medium text-navy">{l.name}</td>
                    <td className="px-4 py-3 text-right font-num">{l.qty} {l.unit}</td>
                    <td className="px-4 py-3 text-right font-num">{usd(l.material_cost_cents + l.labor_cost_cents)}</td>
                    <td className="px-4 py-3 text-right font-num">{usd(l.cost_cents)}</td>
                    <td className="px-4 py-3 text-right font-num text-slate-500">{l.target_margin_pct}%</td>
                    <td className={`px-4 py-3 text-right font-num font-bold ${l.gp_pct < 30 ? "text-dangerr" : "text-successg"}`}>{l.gp_pct}%</td>
                    <td className="px-4 py-3 text-right font-num font-bold text-navy">{usd(l.selling_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end">
            <Btn variant="red" onClick={toProposal} testid="to-proposal"><FileSignature className="w-4 h-4" /> Push {tLabel(tier)} to Proposal</Btn>
          </div>
        </>
      )}
    </div>
  );
}
